// @ts-check
// gameApi.js – centralised wrapper for Game SDK endpoints
// ---------------------------------------------------------------------------
// PURPOSE
// 1. Provide a single import path (`import * as gameApi from '../utils/gameApi'`)
//    so future refactors do not leak raw fetch/buildApiUrl logic across views.
// 2. Read the canonical env var `VITE_GAME_API_URL` but gracefully fall back
//    to the existing auto-detection in api.js when not set (local dev/tests).
// 3. Expose thin helpers (get, post, ws) that forward to the generic helpers
//    in `api.js`, ensuring long-term consistency (headers, auth, retries).
// ---------------------------------------------------------------------------

import {
  buildApiUrl,
  authFetchWithRetry,
  apiGet as _apiGet,
  apiPost as _apiPost,
  apiWs as _apiWs,
} from './api.js';

// ---------------------------------------------------------------------------
// Base resolver – identical to api.js#getApiBase but hard-wired to
// VITE_GAME_API_URL when defined.
// ---------------------------------------------------------------------------

/**
 * Resolve the root URL for game endpoints.
 * Prefers `VITE_GAME_API_URL`, falls back to current origin (/api proxy).
 *
 * @returns {string} Base URL without trailing slash.
 */
export function getGameApiBase() {
  const explicit = import.meta.env.VITE_GAME_API_URL;
  if (explicit && explicit.trim() !== '') {
    return explicit.replace(/\/+$/, ''); // strip trailing slashes
  }
  // Fallback to loan-shark base detection (dev proxy / prod default)
  return buildApiUrl('').replace(/\/+$/, '');
}

// ---------------------------------------------------------------------------
// Helper to build full URL for GAME endpoints (always prefixed with /api/game)
// ---------------------------------------------------------------------------
/**
 * Build a full absolute URL pointing to a game endpoint.
 *
 * @param {string} [endpoint=''] Endpoint path starting with `/`.
 * @returns {string} Absolute URL.
 */
export function buildGameUrl(endpoint = '') {
  if (!endpoint.startsWith('/')) endpoint = `/${endpoint}`;
  const base = getGameApiBase();
  return `${base}${endpoint}`;
}

// ---------------------------------------------------------------------------
// Re-export fetch helpers already wired with the correct base.
// They accept the *endpoint* only (starting with "/").
// ---------------------------------------------------------------------------
/**
 * Perform a `GET` request against the game API.
 *
 * @param {string} endpoint  Path starting with `/`.
 * @param {RequestInit} [opts]
 * @returns {Promise<any>} Parsed JSON response.
 */
export async function gameGet(endpoint, opts = {}) {
  return _apiGet(buildGameUrl(endpoint), opts);
}

/**
 * Perform a `POST` request against the game API.
 *
 * @param {string} endpoint
 * @param {any} [body]
 * @param {RequestInit} [opts]
 * @returns {Promise<any>} Parsed JSON response.
 */
export async function gamePost(endpoint, body = {}, opts = {}) {
  return _apiPost(buildGameUrl(endpoint), body, opts);
}

/**
 * Open a WebSocket channel to a game endpoint.
 *
 * @param {string} endpoint  Path starting with `/`.
 * @returns {WebSocket}
 */
export function gameWs(endpoint) {
  return _apiWs(buildGameUrl(endpoint));
}

// ---------------------------------------------------------------------------
// Business Logic Functions
// ---------------------------------------------------------------------------

/**
 * Get player's current stats
 * @returns {Promise<Object>} - Player stats
 */
export async function getPlayerStats(walletAddress) {
  const url = buildApiUrl(
    walletAddress ? `/api/v1/players/${walletAddress}/stats` : '/api/v1/players/stats'
  );
  const response = await authFetchWithRetry(url);
  return response.json();
}

/**
 * Get game leaderboard
 * @param {string|Object} category - Leaderboard category (e.g., 'wealth', 'reputation') or options object
 * @param {number} limit - Number of entries to return (if category is string)
 * @returns {Promise<Array>} - Leaderboard entries
 */
export async function getLeaderboard(category = 'public', limit = 10) {
  // Support both positional args and options object
  let cat = category;
  let lim = limit;

  if (typeof category === 'object' && category !== null) {
    cat = category.category || 'public';
    lim = category.limit || 10;
  }

  const slug = cat === 'wealth' ? 'public' : cat;
  const url = buildApiUrl(`/api/v1/leaderboard/${slug}?limit=${lim}`);
  const response = await authFetchWithRetry(url);
  return response.json();
}

/**
 * Get first encounter actions for new players (public endpoint)
 * @returns {Promise<Object>} - First encounter action suggestions
 */
export async function getFirstEncounter() {
  const url = buildApiUrl('/api/v1/encounters/first');
  const response = await fetch(url);
  return response.json();
}
